import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNextAvailableApi, getPublicFacilityRulesApi } from '../lib/api';
import { formatTimeRange, formatDate, formatHour } from '../lib/format';
import { Clock, MapPin, ChevronRight, Calendar, ArrowRight, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

export default function PlayerHome() {
  const navigate = useNavigate();
  const [nextSlot, setNextSlot] = useState(null);
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNextAvailable();
  }, []);

  const loadNextAvailable = async () => {
    try {
      const [slotRes, rulesRes] = await Promise.all([
        getNextAvailableApi(),
        getPublicFacilityRulesApi(),
      ]);
      setNextSlot(slotRes.data);
      setRules(rulesRes.data);
    } catch (err) {
      console.error('Failed to load next available:', err);
    }
    setLoading(false);
  };

  const dayRate = rules?.day_rate ?? 250;
  const eveningRate = rules?.evening_rate ?? 350;
  const eveningStart = rules?.evening_start_hour ?? 17;
  const dayStart = rules?.operating_start_hour ?? 6;

  const getAvailabilityCopy = (slot) => {
    if (!slot) return '';
    const count = slot.available_courts;
    if (count === 0) return 'Fully booked';
    if (count === 1) return '1 court left';
    return `${count} courts available`;
  };

  const handleBook = () => {
    if (!nextSlot || nextSlot.availability_state === 'fully_booked') return;
    navigate('/book', { state: { preselected: nextSlot } });
  };

  return (
    <div className="min-h-screen bg-app-bg" data-testid="player-home">
      {/* Hero Header */}
      <div
        className="relative overflow-hidden"
        style={{ background: '#313638' }}
      >
        {/* Desktop: horizontal layout, Mobile: stacked centered */}
        <div className="relative max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 py-12 sm:py-12">
          {/* Mobile: stacked, centered */}
          <div className="flex flex-col items-center text-center sm:flex-row sm:items-start sm:text-left sm:gap-6">
            <img
              src="/logo-hero.png"
              alt="TP Pickleball"
              className="h-14 w-auto sm:h-16 shrink-0 mb-4 sm:mb-0 sm:mt-[-2px]"
              data-testid="hero-logo"
            />
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white leading-tight mb-1.5">
                When can I play next?
              </h1>
              <p className="text-white/50 text-sm">Choose how you want to play. Book in seconds.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-5 max-w-md mx-auto pt-6 pb-24 space-y-5">
        {/* Next Available Card */}
        {loading ? (
          <div className="bg-white rounded-3xl shadow-sm border border-black/5 p-6 space-y-4" data-testid="loading-skeleton">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-12 w-full rounded-full" />
          </div>
        ) : nextSlot && !nextSlot.message ? (
          <div
            className="bg-white rounded-3xl shadow-lg border border-black/5 p-6 animate-slide-up"
            data-testid="next-slot-card"
          >
            <div className="mb-4">
              <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Next available</span>
            </div>

            <div className="mb-4">
              <p className="font-heading text-2xl font-bold text-charcoal mb-1" data-testid="next-slot-time">
                {formatTimeRange(nextSlot.start_hour, nextSlot.end_hour)}
              </p>
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDate(nextSlot.date)}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-5 p-3 rounded-2xl bg-brand-teal-tint">
              <MapPin className="w-4 h-4 text-brand-teal" />
              <span className="text-sm font-medium text-brand-teal" data-testid="next-slot-court">
                {getAvailabilityCopy(nextSlot)}
              </span>
            </div>

            <div className="mb-5 rounded-2xl border border-black/5 divide-y divide-black/5" data-testid="next-slot-rate">
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-sm text-gray-500">Daytime · {formatHour(dayStart)}–{formatHour(eveningStart)}</span>
                <span className="font-heading font-semibold text-charcoal" data-testid="rate-daytime">₱{dayRate.toLocaleString()}/hr</span>
              </div>
              <div className="flex items-center justify-between px-4 py-2.5">
                <span className="text-sm text-gray-500">Evening · after {formatHour(eveningStart)}</span>
                <span className="font-heading font-semibold text-charcoal" data-testid="rate-evening">₱{eveningRate.toLocaleString()}/hr</span>
              </div>
            </div>

            <Button
              data-testid="book-this-slot-btn"
              onClick={handleBook}
              className="w-full bg-brand-teal hover:bg-brand-teal-hover text-white rounded-full py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all active:scale-[0.98]"
            >
              Book This Time
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-3xl shadow-sm border border-black/5 p-8 text-center animate-slide-up" data-testid="no-slots-card">
            <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="font-heading text-lg font-semibold text-charcoal mb-1">All booked up</p>
            <p className="text-sm text-gray-500">No open times in the next 7 days. Check back soon!</p>
          </div>
        )}

        {/* View Other Times */}
        <button
          data-testid="view-other-times-btn"
          onClick={() => navigate('/book')}
          className="w-full flex items-center justify-between px-5 py-4 bg-white rounded-2xl border border-black/5 hover:shadow-sm transition-all active:scale-[0.98]"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-teal-tint flex items-center justify-center">
              <Clock className="w-5 h-5 text-brand-teal" />
            </div>
            <span className="text-sm font-medium text-gray-700">View other times</span>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </button>

        {/* Staff Login — low emphasis */}
        <div className="pt-4 text-center">
          <button
            data-testid="staff-login-btn"
            onClick={() => navigate('/login')}
            className="text-xs text-gray-400 hover:text-brand-teal transition-colors"
          >
            Staff Login
          </button>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 pb-8 text-center border-t border-gray-200">
          <p className="text-[15px] font-heading font-semibold text-text-primary mb-3">Turning Point Pickleball</p>
          <div className="flex flex-col items-center gap-2">
            <a
              href="https://maps.app.goo.gl/wWunrWp73oxkzJDd6"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-address-link"
              className="inline-flex items-start gap-1 text-[13px] text-[#8B949E] leading-relaxed hover:text-brand-teal transition-colors"
            >
              <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>BOL Turning Point<br />Lower Batinguel, Dumaguete City</span>
            </a>
            <a
              href="tel:09173043229"
              data-testid="footer-phone-link"
              className="inline-flex items-center gap-1.5 text-[13px] text-[#8B949E] hover:text-brand-teal transition-colors"
            >
              <Phone className="w-3.5 h-3.5 shrink-0" />
              0917 304 3229
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
